import React, { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { sessionService } from '../services/sessionService';

interface SessionContextType {
    sessionId: string | null;
    isNewSession: boolean;
    resetSession: () => void;
}

const SessionContext = createContext<SessionContextType>({
    sessionId: null,
    isNewSession: true,
    resetSession: () => {},
});

export const SessionProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [sessionId, setSessionId] = useState<string | null>(null);
    const [isNewSession, setIsNewSession] = useState(true);

    useEffect(() => {
        const existingSessionId = sessionService.getSessionId();
        setSessionId(existingSessionId);
        setIsNewSession(!sessionService.hasExistingSession());
    }, []);

    const resetSession = () => {
        sessionService.clearSession();
        const newSessionId = sessionService.getSessionId();
        setSessionId(newSessionId);
        setIsNewSession(true);
    };

    return (
        <SessionContext.Provider value={{
            sessionId,
            isNewSession,
            resetSession,
        }}>
            {children}
        </SessionContext.Provider>
    );
};

export const useSession = () => {
    const context = useContext(SessionContext);
    if (!context) {
        throw new Error('useSession must be used within a SessionProvider');
    }
    return context;
};